<template>
    <div>
        <div class="shadow" v-if="visible">
            <div class="purchaseAuthLetterDialog">
                <img src="@/assets/calendar/日历关闭.png" alt="" class="purchaseAuthLetterClose" @click="close">
                <div class="purchaseAuthLetterTitle">上传授权委托书</div>
                <div class="purchaseAuthLetterIntro">
                    <template v-if="mode === 'not_upload'">
                        根据平台管理要求，{{ isEnterprise ? '机构企业' : '教育科研' }}用户购买遥感商城产品需要签署授权委托书并盖章上传，审核通过后可享受专属购买权益。
                    </template>
                    <template v-else-if="mode === 'viewing'">
                        您上传的{{ isEnterprise ? '机构企业' : '教育科研' }}授权委托书正在审核中，请耐心等待。
                    </template>
                    <template v-else>
                        <div>很抱歉，您的{{ isEnterprise ? '机构企业' : '教育科研' }}授权委托书未通过审核，请重新上传。</div>
                        <div v-if="rejectLine" class="purchaseAuthLetterRejectLine">{{ rejectLine }}</div>
                    </template>
                </div>
                <div
                    class="purchaseAuthLetterDrop"
                    :class="{ purchaseAuthLetterDropActive: dragOver }"
                    @click="triggerFile"
                    @dragover.prevent="dragOver = true"
                    @dragleave.prevent="dragOver = false"
                    @drop.prevent="onDrop"
                >
                    <div class="purchaseAuthLetterDropIcon">
                        <img src="@/assets/OpenEducationalCertification/教育认证-上传材料.png" alt="" width="66" height="66">
                    </div>
                    <div class="purchaseAuthLetterDropMain">点击或将文件拖拽到这里上传</div>
                    <div class="purchaseAuthLetterDropSub">支持扩展名：.pdf .jpg .png .jpeg</div>
                    <input
                        ref="fileInput"
                        type="file"
                        class="purchaseAuthLetterFileInput"
                        accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                        @change="onFileChange"
                    >
                </div>
                <div v-if="uploaded && uploadedFile" class="purchaseAuthLetterFileRow">
                    <span class="purchaseAuthLetterFileName">{{ uploadedFile.name }}</span>
                    <span class="purchaseAuthLetterFileRemove" @click.stop="removeFile">×</span>
                </div>
                <a
                    class="purchaseAuthLetterTemplateLink"
                    href="javascript:;"
                    @click.prevent="downloadTemplate"
                >{{ templateLoading ? '正在获取…' : '点击下载授权委托书模版' }}</a>
                <div class="purchaseAuthLetterFooter">
                    <div class="purchaseAuthLetterBtnPrimary" @click="submit">{{ primaryLabel }}</div>
                    <div class="purchaseAuthLetterBtnGhost" @click="close">暂不上传</div>
                </div>
            </div>
        </div>
        <!--
            顶部 toast 必须放在 v-if="visible" 之外，否则 dialog 关闭后整个 DOM 被卸载，
            toast 永远不可见；保留时数据又会让用户在下一次唤醒时看到错位信息。
            用 position: fixed 脱离 dialog 生命周期，并在 showTip 中 setTimeout 自动消失。
        -->
        <div v-if="topTip" class="purchaseAuthLetterTopTip">
            <div
                class="purchaseAuthLetterTopTipInner"
                :class="'purchaseAuthLetterTopTipInner--' + topTipType"
            >
                <span class="purchaseAuthLetterTopTipMark" v-if="topTipType === 'success'">✓</span>
                <span class="purchaseAuthLetterTopTipMark" v-else>×</span>
                <span class="purchaseAuthLetterTopTipText">{{ topTip }}</span>
            </div>
        </div>
    </div>
</template>

<script>
/**
 * 授权委托书弹窗 component（与 jl1store_front 同款样式 + 同款 API 形态）
 *
 * Props:
 *   visible (Boolean)             - 弹窗可见性
 *   mode (String)                 - 'not_upload' | 'viewing' | 'rejected'
 *   rejectReason (String)         - 仅 rejected 时展示
 *   userIdentityReviewStatus (Number) - 状态码 0~13，内部计算 isEnterprise
 *
 * Emits:
 *   update:visible - 关闭弹窗
 *   success        - 上传成功（父组件按文件类型决定后续行为）
 *   fail           - 上传失败（父组件顶层 toast 由它自己决定）
 */
export default {
    name: 'PurchaseAuthLetterDialog',
    props: {
        visible: { type: Boolean, required: true, default: false },
        mode: { type: String, required: true, default: 'not_upload' },
        rejectReason: { type: String, default: '' },
        userIdentityReviewStatus: { type: Number, default: null },
    },
    data() {
        return {
            dragOver: false,
            uploaded: false,
            uploadedFile: undefined,
            uploading: false,
            templateLoading: false,
            topTip: '',
            topTipType: 'success',
        };
    },
    computed: {
        isEnterprise() {
            return [4, 5, 7].includes(this.userIdentityReviewStatus);
        },
        rejectLine() {
            if (this.mode !== 'rejected') return '';
            const r = (this.rejectReason || '').trim();
            return r ? ('未通过原因：' + r) : '';
        },
        primaryLabel() {
            return this.mode === 'not_upload' ? '确认上传' : '重新上传';
        },
    },
    watch: {
        visible(v) {
            if (!v) this.removeFile();
        },
        mode() {
            this.removeFile();
        },
    },
    methods: {
        getKey() {
            return localStorage.getItem('token');
        },
        showTip(msg, type) {
            this.topTip = msg || '';
            this.topTipType = type === 'success' ? 'success' : 'error';
            // 自动消失，避免下次唤醒弹窗时残留
            if (this._topTipTimer) clearTimeout(this._topTipTimer);
            this._topTipTimer = setTimeout(() => {
                this.clearTip();
                this._topTipTimer = null;
            }, 2500);
        },
        clearTip() {
            this.topTip = '';
        },
        close() {
            this.removeFile();
            this.clearTip();
            this.$emit('update:visible', false);
        },
        triggerFile() {
            const el = this.$refs.fileInput;
            if (el) {
                el.value = '';
                el.click();
            }
        },
        onDrop(e) {
            this.dragOver = false;
            const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
            this.assignFile(f);
        },
        onFileChange(e) {
            const f = e.target && e.target.files && e.target.files[0];
            this.assignFile(f);
        },
        assignFile(file) {
            if (!file) return;
            this.uploadedFile = file;
            this.uploaded = true;
        },
        removeFile() {
            this.uploadedFile = undefined;
            this.uploaded = false;
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = '';
            }
        },
        validateFile() {
            const f = this.uploadedFile;
            if (!f) {
                this.showTip('上传失败，文件大小/格式不符合要求', 'error');
                return false;
            }
            const maxBytes = 20 * 1024 * 1024;
            if (f.size > maxBytes) {
                this.showTip('上传失败，文件大小/格式不符合要求', 'error');
                return false;
            }
            const n = f.name.toLowerCase();
            const ok = n.endsWith('.pdf') || n.endsWith('.jpg') || n.endsWith('.jpeg') || n.endsWith('.png');
            if (!ok) {
                this.showTip('上传失败，文件大小/格式不符合要求', 'error');
                return false;
            }
            return true;
        },
        downloadTemplate() {
            if (this.templateLoading) return;
            this.clearTip();
            this.templateLoading = true;
            const url = this.isEnterprise
                ? '/accessapi/enterprise/cert/getAuthorizationLetter'
                : '/accessapi/edu/cert/getAuthorizationLetter';
            this.axios.post(url, {}, {
                headers: {
                    Authorization: 'Bearer ' + this.getKey(),
                },
            }).then((res) => {
                const body = res.data || {};
                if (body.status === 200 && body.data) {
                    const url = typeof body.data === 'string' ? body.data : (body.data.url || body.data);
                    if (url) {
                        window.open(url, '_blank');
                    } else {
                        this.showTip('未获取到模板地址', 'error');
                    }
                } else {
                    this.showTip((body.message && String(body.message)) || '获取模板失败，请稍后重试', 'error');
                }
            }).catch(() => {
                this.showTip('获取模板失败，请稍后重试', 'error');
            }).finally(() => {
                this.templateLoading = false;
            });
        },
        submit() {
            if (this.uploading) return;
            if (!this.validateFile()) {
                this.$emit('fail');
                return;
            }
            this.clearTip();
            const url = this.isEnterprise
                ? '/accessapi/enterprise/cert/uploadAuthorizationLetter'
                : '/accessapi/edu/cert/uploadAuthorizationLetter';
            const fd = new FormData();
            fd.append('authorizationLetter', this.uploadedFile);
            this.uploading = true;
            this.axios.post(url, fd, {
                headers: {
                    Authorization: 'Bearer ' + this.getKey(),
                    'Content-Type': 'multipart/form-data',
                },
            }).then((res) => {
                const d = res.data || {};
                if (d.status != null && d.status !== 200) {
                    this.showTip('上传失败，文件大小/格式不符合要求', 'error');
                    this.$emit('fail');
                    return;
                }
                if (d.code != null && d.code !== 200 && d.code !== 0) {
                    this.showTip('上传失败，文件大小/格式不符合要求', 'error');
                    this.$emit('fail');
                    return;
                }
                if (d.success === false) {
                    this.showTip('上传失败，文件大小/格式不符合要求', 'error');
                    this.$emit('fail');
                    return;
                }
                this.showTip('上传成功，预计在1-3个工作日内完成审核', 'success');
                this.$emit('update:visible', false);
                this.$emit('success');
            }).catch(() => {
                this.showTip('上传失败，文件大小/格式不符合要求', 'error');
                this.$emit('fail');
            }).finally(() => {
                this.uploading = false;
            });
        },
    },
};
</script>

<style scoped>
.purchaseAuthLetterDialog {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 560px;
    max-width: 92vw;
    box-sizing: border-box;
    padding: 48px 40px 32px;
    background: #FFFFFF;
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
}
.purchaseAuthLetterClose {
    position: absolute;
    top: 20px;
    right: 24px;
    width: 20px;
    height: 20px;
    cursor: pointer;
    z-index: 1;
}
.purchaseAuthLetterTitle {
    text-align: center;
    font-size: 20px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.85);
    line-height: 28px;
    margin-bottom: 16px;
}
.purchaseAuthLetterIntro {
    font-size: 14px;
    line-height: 22px;
    color: rgba(0, 0, 0, 0.65);
    text-align: center;
    margin-bottom: 20px;
}
.purchaseAuthLetterRejectLine {
    margin-top: 8px;
    color: rgba(0, 0, 0, 0.65);
}
.purchaseAuthLetterDrop {
    position: relative;
    border: 1px dashed #D9D9D9;
    border-radius: 8px;
    padding: 28px 16px;
    text-align: center;
    cursor: pointer;
    background: #FAFAFA;
    transition: border-color 0.2s, background 0.2s;
}
.purchaseAuthLetterDropActive {
    border-color: #409EFF;
    background: #f0f7ff;
}
.purchaseAuthLetterDropIcon {
    margin-bottom: 12px;
}
.purchaseAuthLetterDropMain {
    font-size: 14px;
    color: rgba(0, 0, 0, 0.85);
    margin-bottom: 8px;
}
.purchaseAuthLetterDropSub {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.45);
}
.purchaseAuthLetterFileInput {
    display: none;
}
.purchaseAuthLetterFileRow {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-top: 12px;
    font-size: 14px;
    color: #409EFF;
}
.purchaseAuthLetterFileName {
    text-decoration: underline;
    max-width: 380px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.purchaseAuthLetterFileRemove {
    cursor: pointer;
    color: rgba(0, 0, 0, 0.45);
    font-size: 18px;
    line-height: 1;
    text-decoration: none;
}
.purchaseAuthLetterTemplateLink {
    display: block;
    text-align: center;
    margin-top: 16px;
    font-size: 14px;
    color: #409EFF;
    text-decoration: underline;
    cursor: pointer;
}
.purchaseAuthLetterFooter {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;
    margin-top: 28px;
}
.purchaseAuthLetterBtnPrimary {
    min-width: 120px;
    padding: 0 24px;
    height: 40px;
    line-height: 40px;
    text-align: center;
    background: #409EFF;
    color: #fff;
    border-radius: 4px;
    font-size: 14px;
    cursor: pointer;
}
.purchaseAuthLetterBtnPrimary:hover {
    background: #66b1ff;
}
.purchaseAuthLetterBtnGhost {
    min-width: 120px;
    padding: 0 24px;
    height: 40px;
    line-height: 40px;
    text-align: center;
    background: #fff;
    color: rgba(0, 0, 0, 0.85);
    border: 1px solid #DCDFE6;
    border-radius: 4px;
    font-size: 14px;
    cursor: pointer;
}
.purchaseAuthLetterBtnGhost:hover {
    border-color: #409EFF;
    color: #409EFF;
}
.purchaseAuthLetterTopTip {
    /* 必须在 v-if="visible" 之外、position: fixed，才能在 dialog 关闭后继续可见 */
    position: fixed;
    top: 20px;
    left: 16px;
    right: 16px;
    /* 高于 .shadow 的 z-index 1000001 */
    z-index: 1000002;
    text-align: center;
    box-sizing: border-box;
    pointer-events: none;
}
.purchaseAuthLetterTopTipInner {
    display: inline-flex;
    align-items: flex-start;
    gap: 8px;
    max-width: 90%;
    padding: 10px 16px;
    border-radius: 4px;
    font-size: 14px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
    pointer-events: auto;
    text-align: left;
}
.purchaseAuthLetterTopTipInner--success {
    background: #f6ffed;
    border: 1px solid #b7eb8f;
    color: rgba(0, 0, 0, 0.88);
}
.purchaseAuthLetterTopTipInner--error {
    background: #fff2f0;
    border: 1px solid #ffccc7;
    color: rgba(0, 0, 0, 0.88);
}
.purchaseAuthLetterTopTipText {
    line-height: 1.4;
    word-break: break-word;
}
.purchaseAuthLetterTopTipMark {
    flex-shrink: 0;
    font-weight: 700;
    line-height: 1.2;
}
.purchaseAuthLetterTopTipInner--success .purchaseAuthLetterTopTipMark {
    color: #52c41a;
}
.purchaseAuthLetterTopTipInner--error .purchaseAuthLetterTopTipMark {
    color: #ff4d4f;
}
</style>
